import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaCheckCircle, FaArrowRight, FaDownload, FaChevronDown, FaChevronUp, FaCalculator } from 'react-icons/fa';
import { 
  determineMaturityStage, 
  generateRecommendations, 
  formatScoresForDisplay 
} from '../../utils/assessmentScoring';
import { generateAssessmentPdf } from '../../utils/pdfGenerator';
import { generateScoreBreakdown } from '../../utils/scoreBreakdown';
import GuidePrintout from './GuidePrintout';

export const AssessmentResults = ({ scores, currentStage, targetStage, onReset, formData, recommendations }) => {
  const printoutRef = useRef(null);
  const [expandedDimension, setExpandedDimension] = useState(null);
  const formattedScores = formatScoresForDisplay(scores);
  const maturityStage = determineMaturityStage(scores.overall);
  
  // Use AI recommendations if provided, otherwise fall back to static
  const finalRecommendations = recommendations || generateRecommendations(scores, currentStage, targetStage).recommendations;
  const scoreBreakdown = formData ? generateScoreBreakdown(formData, scores) : null;

  const getScoreColor = (score) => {
    if (score >= 4) return 'text-green-400';
    if (score >= 3) return 'text-yellow-400';
    if (score >= 2) return 'text-orange-400';
    return 'text-red-400';
  };

  const toggleDimension = (dimension) => {
    setExpandedDimension(expandedDimension === dimension ? null : dimension);
  };

  const handleDownloadPdf = async () => {
    await generateAssessmentPdf('assessment-report', 'AI_Readiness_Assessment_Results.pdf');
  };

  // Prepare results data for PDF
  const pdfResults = {
    overallScore: scores.overall,
    stage: maturityStage,
    dimensionScores: {
      strategy: scores.strategy,
      data: scores.data,
      technology: scores.technology,
      people: scores.people,
      governance: scores.governance
    },
    recommendations: finalRecommendations.map(rec => ({
      dimension: rec.dimension,
      priority: rec.priority,
      recommendation: rec.action,
      impact: rec.impact
    })),
    formData: formData // Include full form data for Q&A section
  };


  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-5xl mx-auto px-6"
      >
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full mb-6"
          >
            <FaTrophy className="text-4xl text-white" />
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-blue-400">
            Assessment Complete!
          </h1>
          
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Thank you for completing the AI Readiness Assessment. Here are your results and personalized recommendations.
          </p>
        </div>

        {/* Overall Score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-gradient-to-r from-teal-600/20 to-blue-600/20 border border-teal-500/30 rounded-xl p-8 mb-8"
        >
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Your AI Maturity Level</h2>
            <div className="flex items-center justify-center gap-8 mb-6">
              <div>
                <div className={`text-6xl font-bold ${getScoreColor(scores.overall)}`}>
                  {scores.overall.toFixed(1)}
                </div>
                <div className="text-gray-400 text-sm">out of 5.0</div>
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-white mb-2">
                  Stage {maturityStage.stage}: {maturityStage.name}
                </div>
                <div className="text-lg text-teal-400">{maturityStage.subtitle}</div>
                <p className="text-gray-300 mt-2">{maturityStage.description}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Dimension Scores */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold mb-6">Dimension Scores</h2>
          <div className="space-y-4">
            {formattedScores.dimensions.map((dimension, index) => (
              <motion.div
                key={dimension.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-semibold">{dimension.name}</span>
                  <span className={`text-2xl font-bold ${getScoreColor(dimension.score)}`}>
                    {dimension.score.toFixed(1)}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(dimension.score / 5) * 100}%` }}
                    transition={{ duration: 1, delay: 0.6 + index * 0.1 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: dimension.color }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold">Personalized Recommendations</h2>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/20 text-teal-400 border border-teal-500/30">
              AI-Powered
            </span>
          </div>
          <div className="space-y-4">
            {finalRecommendations.map((rec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.9 + index * 0.1 }}
                className={`p-6 rounded-lg border ${
                  rec.priority === 'High' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-start gap-4">
                  <FaCheckCircle className={`text-2xl mt-1 ${
                    rec.priority === 'High' ? 'text-red-400' : 'text-yellow-400'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{rec.dimension}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        rec.priority === 'High' 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {rec.priority} Priority
                      </span>
                    </div>
                    <p className="text-gray-300 mb-2">{rec.action}</p>
                    <p className="text-sm text-gray-400 italic">Impact: {rec.impact}</p>
                    {rec.rationale && (
                      <p className="text-sm text-teal-400 mt-3 pl-4 border-l-2 border-teal-500/30">
                        {rec.rationale}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Scoring Transparency Section */}
        {scoreBreakdown && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-8 mb-8 print-hidden"
          >
            <div className="flex items-center gap-3 mb-6">
              <FaCalculator className="text-2xl text-blue-400" />
              <h2 className="text-2xl font-bold">How Your Score Was Calculated</h2>
            </div>
            <p className="text-gray-300 mb-6">
              Expand each dimension below to see exactly which questions contributed to your score.
              Full transparency means you can trust and act on these results.
            </p>

            <div className="space-y-3">
              {Object.entries(scoreBreakdown).map(([dimensionKey, dimensionData]) => {
                const isExpanded = expandedDimension === dimensionKey;
                const dimensionName = dimensionKey.charAt(0).toUpperCase() + dimensionKey.slice(1);
                
                return (
                  <div key={dimensionKey} className="border border-gray-700 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleDimension(dimensionKey)}
                      className="w-full flex items-center justify-between p-4 bg-gray-800/50 hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-lg font-semibold">{dimensionName}</span>
                        <span className={`text-2xl font-bold ${getScoreColor(dimensionData.score)}`}>
                          {dimensionData.score.toFixed(2)}
                        </span>
                        <span className="text-sm text-gray-400">
                          ({dimensionData.fields.length} {dimensionData.fields.length === 1 ? 'question' : 'questions'})
                        </span>
                      </div>
                      {isExpanded ? (
                        <FaChevronUp className="text-gray-400" />
                      ) : (
                        <FaChevronDown className="text-gray-400" />
                      )}
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-gray-900/50"
                        >
                          <div className="p-4 space-y-3">
                            {dimensionData.fields.map((field, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/30 rounded border border-gray-700/50">
                                <div className="flex-1">
                                  <div className="font-medium text-white mb-1">{field.label}</div>
                                  <div className="text-sm text-gray-400">{field.value}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className={`text-lg font-bold ${getScoreColor(field.score)}`}>
                                    {field.score.toFixed(1)}
                                  </span>
                                  <span className="text-xs text-gray-500">/ 5.0</span>
                                </div>
                              </div>
                            ))}
                            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                              <div className="text-sm text-gray-300">
                                <strong className="text-blue-400">Average:</strong> ({
                                  dimensionData.fields.map(f => f.score.toFixed(1)).join(' + ')
                                }) / {dimensionData.fields.length} = <strong className={getScoreColor(dimensionData.score)}>
                                  {dimensionData.score.toFixed(2)}
                                </strong>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="bg-gradient-to-r from-teal-600/20 to-blue-600/20 border border-teal-500/30 rounded-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">Ready to Take Action?</h2>
          <p className="text-gray-300 text-center mb-8 max-w-2xl mx-auto">
            We've saved your assessment results. Let's discuss how we can help you advance your AI maturity and achieve your goals.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all text-white shadow-lg hover:shadow-xl"
            >
              Schedule a Consultation <FaArrowRight />
            </motion.a>
            
            <motion.button
              onClick={handleDownloadPdf}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-gray-500 rounded-lg font-semibold hover:border-gray-400 transition-all text-white"
            >
              <FaDownload /> Download Results & Framework PDF
            </motion.button>
          </div>
        </motion.div>

        {/* Reset Option */}
        <div className="text-center">
          <button
            onClick={onReset}
            className="text-gray-400 hover:text-white transition-colors underline"
          >
            Take the assessment again
          </button>
        </div>
      </motion.div>

      {/* Hidden PDF Template */}
      <div className="fixed -left-[9999px] top-0">
        <GuidePrintout ref={printoutRef} results={pdfResults} />
      </div>
    </div>
  );
};

